import { useState } from "react";
import { useSeo } from "@/lib/seo";
import { Link } from "react-router-dom";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { COLLAB } from "@/constants/testIds";

const field = "w-full px-5 py-3.5 rounded-full border border-[#E7E0D3] bg-white focus:outline-none focus:ring-2 focus:ring-[#E06D44]/40";
const FOLLOWERS = ["Under 2k followers", "2k – 10k followers", "10k – 50k followers", "50k+ followers"];
const PETS = ["Dog account", "Cat account", "Dogs & cats"];

const Select = ({ value, onChange, options, testId }) => (
  <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={field}>
    {options.map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
  </select>
);

export default function CollabPage() {
  useSeo({ title: "Pet Creator Collabs: Earn With Your Pet | PetTok Shop", description: "Pet creators on TikTok and Instagram: get your own discount code, free Kiddee-approved products and earnings on every sale.", path: "/collab" });
  const [f, setF] = useState({ name: "", email: "", handle: "", platform: "tiktok", followers: FOLLOWERS[1], pet_type: PETS[0] });
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const link = res ? `${window.location.origin}/?ref=${res.handle}` : "";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/collab", f);
      setRes(data);
    } catch (err) {
      setError(errMsg(err));
    }
  };
  const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success("Copied"));

  return (
    <main data-testid={COLLAB.page} className="pt-32 pb-10 max-w-3xl mx-auto px-6">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E06D44]">Creator collabs</span>
      <h1 className="font-heading mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">Your pet's famous. <span className="text-[#E06D44]">Get paid for it.</span></h1>
      <p className="mt-5 text-stone-600 text-base md:text-lg">Join the PetTok Shop pack. You get a personal link and a 10% discount code for your audience. Kiddee approves.</p>
      {res ? (
        <div data-testid={COLLAB.result} className="mt-10 bg-[#1C1917] text-white rounded-[2rem] p-8 space-y-5">
          <p className="font-heading text-2xl font-bold">Welcome to the pack, @{res.handle}!</p>
          {[["Your 10% code", res.code, COLLAB.code], ["Your link", link, COLLAB.link]].map(([label, value, id]) => (
            <div key={id}>
              <p className="text-sm text-white/60">{label}</p>
              <button data-testid={id} onClick={() => copy(value)} className="mt-1 w-full flex items-center justify-between gap-3 bg-white/10 hover:bg-white/20 px-5 py-3 rounded-full text-left break-all transition-colors">
                {value} <Copy size={16} className="shrink-0" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 bg-white border border-[#F0EBE1] rounded-[2rem] p-6 sm:p-8 grid sm:grid-cols-2 gap-3">
          <input data-testid={COLLAB.name} required value={f.name} onChange={set("name")} placeholder="Your name" className={field} />
          <input data-testid={COLLAB.email} required type="email" value={f.email} onChange={set("email")} placeholder="your@email.com" className={field} />
          <input data-testid={COLLAB.handle} required value={f.handle} onChange={set("handle")} placeholder="@yourhandle" className={field} />
          <Select testId={COLLAB.platform} value={f.platform} onChange={set("platform")} options={[["tiktok", "TikTok"], ["instagram", "Instagram"]]} />
          <Select testId={COLLAB.followers} value={f.followers} onChange={set("followers")} options={FOLLOWERS.map((o) => [o, o])} />
          <Select testId={COLLAB.pet} value={f.pet_type} onChange={set("pet_type")} options={PETS.map((o) => [o, o])} />
          <button data-testid={COLLAB.submit} className="sm:col-span-2 py-3.5 rounded-full bg-[#E06D44] text-white font-medium hover:bg-[#C95B33] transition-colors">Get my link + code</button>
          {error && <p data-testid={COLLAB.error} className="sm:col-span-2 text-sm text-[#E11D48]">{error}</p>}
        </form>
      )}
      <p className="mt-6 text-sm text-stone-600">Already in the pack? <Link to="/collab/stats" data-testid={COLLAB.statsLink} className="underline text-[#E06D44]">Check your stats</Link></p>
      <div className="mt-10 grid sm:grid-cols-2 gap-6 text-sm text-stone-600">
        <div><h2 className="font-heading text-lg font-bold text-[#1C1917]">What you get</h2><p className="mt-2">A personal link and a 10% code for your audience, plus first dibs on free product drops for top performers.</p></div>
        <div><h2 className="font-heading text-lg font-bold text-[#1C1917]">What we look for</h2><p className="mt-2">Real pets, real engagement. 2k–20k followers is our sweet spot. A comment section full of regulars beats 100k ghost followers.</p></div>
      </div>
    </main>
  );
}
